import * as THREE from 'three';
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';

// ==========================================
// VARIANTS DE CONSTEL·LACIONS (Coordinates 2D)
// ==========================================
// Hem passat a usar només X i Y (Z serà sempre 0). 
// Això fa que els grafs siguin absolutament plans.
const variants = [
    // 1. Variant Àries (Zig-Zag)
    [[-2, 1], [-1, -1], [0, 0.5], [1, -0.5], [2, 1]],
    // 2. Variant Ossa Major ("El cullerot")
    [[-3, 1], [-2, 0.5], [-1, 0.2], [0, 0], [1, -1], [2.5, -0.8], [1.5, 0.5], [0, 0]],
    // 3. Variant Sagitàrius ("Fletxa apuntant cap amunt")
    [[-2, -2], [0, 0], [2, 2], [1, 2.5], [2, 2], [2.5, 1]],
    // 4. Variant Leo (Ganxo i cos panxut)
    [[2, 1], [1, 2], [0, 1.5], [0.5, 0], [-1, -1], [-2, -0.5], [-3, 0.5], [-2, 1.5], [-1.5, 0.5]],
    // 5. Variant Orió (Caçador amb cinturó al mig)
    [[-1, 3], [1, 3], [0, 1], [-0.5, 0], [0, -0.2], [0.5, 0], [0, 1], [-1, -2], [1, -2]]
];

// Farciment de punts pel cel
function StarsBackground() {
    const { positions, colors } = useMemo(() => {
        const count = 2000;
        const radius = 350;
        const posArray = new Float32Array(count * 3);
        const colArray = new Float32Array(count * 3);
        const baseColor = new THREE.Color("#ffffff");

        for (let i = 0; i < count; i++) {
            // Algorisme de distribució esfèrica uniforme
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos((Math.random() * 2) - 1);

            posArray[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
            posArray[i * 3 + 1] = radius * Math.cos(phi);
            posArray[i * 3 + 2] = radius * Math.sin(phi) * Math.sin(theta);

            // Simulem opacitat generant colors més clars o més foscos
            const brightness = 0.05 + Math.random() * 0.4; // Fons tènue
            colArray[i * 3] = baseColor.r * brightness;
            colArray[i * 3 + 1] = baseColor.g * brightness;
            colArray[i * 3 + 2] = baseColor.b * brightness;
        }
        return { positions: posArray, colors: colArray };
    }, []);

    return (
        <points>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" count={positions.length / 3} array={positions} itemSize={3} />
                <bufferAttribute attach="attributes-color" count={colors.length / 3} array={colors} itemSize={3} />
            </bufferGeometry>
            <pointsMaterial
                size={1.5}
                sizeAttenuation={false}
                vertexColors
                transparent
                depthWrite={false}
                blending={THREE.AdditiveBlending}
            />
        </points>
    );
}

// ==========================================
// SKYBOX DE CONSTELLACIONS (Efecte Planetarium)
// ==========================================
export function ZodiacBackground() {
    const skyRef = useRef();

    // RENDIMENT SÈNIOR: Dibuixar 45 grups de `<line>` i `<points>` costa 90 Draw Calls per frame!
    // Instanciem MASCARAMENT els grafs 2D combinant tots els seus vèrtexs en nomes DOS BufferGeometries. 
    // Reducció de Draw Calls per Constel·lacions de 90 a només 2!
    const { lineGeom, pointsGeom } = useMemo(() => {
        const radius = 300;
        const density = 45;

        const linePositions = [];
        const pointPositions = [];

        for (let i = 0; i < density; i++) {
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos((Math.random() * 2) - 1);

            const x = radius * Math.sin(phi) * Math.cos(theta);
            const y = radius * Math.cos(phi);
            const z = radius * Math.sin(phi) * Math.sin(theta);

            // Matriu de manipulació falsa per alinear-ho a la cúpula sense crear Objects3D inútils a l'escena
            const dummy = new THREE.Object3D();
            dummy.position.set(x, y, z);
            dummy.lookAt(0, 0, 0);
            dummy.rotateZ(Math.random() * Math.PI * 2);
            dummy.scale.setScalar(5 + Math.random() * 8);
            dummy.updateMatrix();

            const variant = variants[Math.floor(Math.random() * variants.length)];

            // Mapem l'array pla i l'inflem matemàticament al voltant de l'esfera
            const mappedPoints = variant.map(p => {
                const vec = new THREE.Vector3(p[0], p[1], 0);
                vec.applyMatrix4(dummy.matrix);
                return vec;
            });

            // 1. Guardar punts de vèrtex
            for (const pt of mappedPoints) {
                pointPositions.push(pt.x, pt.y, pt.z);
            }

            // 2. Guardar Linia contínua (LineSegments requereix A-B, B-C, etc.)
            for (let j = 0; j < mappedPoints.length - 1; j++) {
                linePositions.push(mappedPoints[j].x, mappedPoints[j].y, mappedPoints[j].z);
                linePositions.push(mappedPoints[j + 1].x, mappedPoints[j + 1].y, mappedPoints[j + 1].z);
            }
        }

        const lGeom = new THREE.BufferGeometry();
        lGeom.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));

        const pGeom = new THREE.BufferGeometry();
        pGeom.setAttribute('position', new THREE.Float32BufferAttribute(pointPositions, 3));

        return { lineGeom: lGeom, pointsGeom: pGeom };
    }, []);

    useFrame((state, delta) => {
        if (skyRef.current) {
            // "INFINITY SKYBOX TRICK"
            // El fons ha de seguir el jugador o la càmera en l'eix de desplaçament.
            // Posant la cúpula exactament a la posició de la càmera a cada frame i deshabilitant la profunditat (Z-buffer)
            // aconseguim que el cel sembli literalment a l'infinit matemàtic, sense acostar-s'hi mai!
            skyRef.current.position.copy(state.camera.position);

            // Una levíssima rotació per donar sensació de sistema estel·lar real (rotació de la Terra)
            skyRef.current.rotation.y += delta * 0.005;
            skyRef.current.rotation.z += delta * 0.002;
        }
    });

    return (
        <group ref={skyRef}>
            <StarsBackground />

            {/* Draw Call únic 1: Les línies de totes les constel·lacions fusionades! */}
            <lineSegments geometry={lineGeom}>
                <lineBasicMaterial color="#00ffff" transparent opacity={0.15} depthWrite={false} blending={THREE.AdditiveBlending} />
            </lineSegments>

            {/* Draw Call únic 2: Els Punts (esfères) de les constel·lacions fusionats! */}
            <points geometry={pointsGeom}>
                <pointsMaterial color="#00ffff" size={3} sizeAttenuation={false} transparent opacity={0.5} depthWrite={false} blending={THREE.AdditiveBlending} />
            </points>
        </group>
    );
}
